import React, { useEffect, useState, useMemo } from "react";
import moment from "moment";
import {
  X,
  AlertCircle,
  User,
  Users,
  Package,
  Calendar as CalendarIcon,
  Clock,
  Edit2,
  Trash2,
  Building2,
  ListFilter,
  Layers,
} from "lucide-react";
import { studentSubscriptionsAPI } from "../../services/api";
import { useTheme } from "../../contexts/ThemeContext";
import { useTranslation } from "react-i18next";
import TimePicker from "../ui/TimePicker";
import LoadingSpinner from "../ui/LoadingSpinner";
import { useAuth } from "../../App";
import { usePermissions } from "../HasPermission";

const SessionModal = ({
  isOpen,
  modalType,
  selectedEvent,
  formData,
  formErrors,
  formError,
  submitting,
  trainers,
  students,
  sessionTypes, // Merged list (Categories + Packages)
  mainSessionTypes = [], // Main Categories
  subPackages = [], // Just Packages
  studentSubscriptions = [],
  loadingSessionTypes,
  loadingStudentSubscriptions,
  sessionTimes,
  onClose,
  onSubmit,
  onDelete,
  onFormDataChange,
  organizationId,
  onSessionTimesChange,
  onAddSessionTime,
  onRemoveSessionTime,
  onUpdateSessionTime,
  handleStudentsChange,
  onEditMode,
  // New props for organization selection
  organizations = [],
  loadingOrganizations = false,
  isAllOrgsMode = false,
  isDemoSession = false,
  onLoadSessionTypes,
}) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const [availableStudents, setAvailableStudents] = useState([]);
  const [studentSearchTerm, setStudentSearchTerm] = useState("");
  const [loadingStudents, setLoadingStudents] = useState(false);

  // Get the effective organization ID (selected org when in 'all' mode)
  const effectiveOrgId = isAllOrgsMode
    ? formData.selectedOrganizationId
    : organizationId;

  // Get selected package info
  const currentPackage = useMemo(() => {
    if (!formData.sessionTypeId) return null;
    return subPackages.find((p) => p.id === formData.sessionTypeId) || sessionTypes.find((t) => t.id === formData.sessionTypeId);
  }, [formData.sessionTypeId, subPackages, sessionTypes]);

  // --- Filtering Logic ---

  // Fetch students by Session Type (Main Category) — shows ALL students with any package under that type
  useEffect(() => {
    const loadStudentsForSessionType = async () => {
      // Use mainTypeId (Session Type) to fetch students, not the specific package
      const typeId = formData.mainTypeId;
      if (
        typeId &&
        (modalType === "create" || modalType === "group_manage") &&
        effectiveOrgId
      ) {
        setLoadingStudents(true);
        try {
          const response = await studentSubscriptionsAPI.getStudentsBySessionType(
            effectiveOrgId,
            typeId,
          );
          setAvailableStudents(response.data.data || []);
        } catch (error) {
          console.error("Error loading students:", error);
          setAvailableStudents([]);
        } finally {
          setLoadingStudents(false);
        }
      } else {
        setAvailableStudents([]);
      }
    };
    loadStudentsForSessionType();
  }, [
    formData.mainTypeId,
    modalType,
    effectiveOrgId,
  ]);

  // 1. Get Active Subscriptions for Selected Student(s)
  const activeStudentSubscriptions = useMemo(() => {
    if (formData.students.length > 0 && studentSubscriptions.length > 0) {
      // Mirror the backend's create rule: a subscription is bookable when it has
      // capacity (remaining > 0) and is not cancelled/expired. A 'completed' sub with
      // remaining left (legacy drift) IS bookable — the backend self-heals it on create.
      // (Old code had an operator-precedence bug: `A && B && C || D` => any sub with
      // remaining>0 passed regardless of status, so cancelled/expired packages showed
      // and then failed on submit.)
      return studentSubscriptions.filter(
        (s) =>
          s.status !== "expired" &&
          s.status !== "cancelled" &&
          Number(s.remainingSessions) > 0
      );
    }
    return [];
  }, [formData.students, studentSubscriptions]);

  // 2. Filter Main Categories (e.g. Reformer, Pilates)
  const availableMainTypes = useMemo(() => {
    if (!mainSessionTypes || mainSessionTypes.length === 0) return [];
    
    // If no student selected, show all categories
    if (formData.students.length === 0) return mainSessionTypes;

    // If we are currently loading, return empty to show spinner/lock
    if (loadingStudentSubscriptions && studentSubscriptions.length === 0) return [];

    // If a package is ALREADY selected, we don't want to filter out the category list
    // because that breaks the UI when trying to change it.
    if (formData.sessionTypeId) return mainSessionTypes;

    const validMainTypeIds = new Set();
    
    // Collect valid session types from subscriptions
    activeStudentSubscriptions.forEach((sub) => {
      // Check directly on subscription first
      const sTypeId = sub.sessionTypeId || sub.session_type_id;
      if (sTypeId) {
        validMainTypeIds.add(sTypeId);
      }
      
      // Check via linked package
      const pkgId = sub.membershipPackageId || sub.packageId || sub.membership_package_id;
      if (pkgId) {
        const pkg = subPackages.find((p) => p.id === pkgId);
        if (pkg && pkg.sessionTypeId) {
          validMainTypeIds.add(pkg.sessionTypeId);
        }
      }
    });

    // If we found specific categories the user owns, filter to those.
    if (validMainTypeIds.size > 0) {
      return mainSessionTypes.filter((t) => validMainTypeIds.has(t.id));
    }

    // No category matched. If the student genuinely has NO active subscription,
    // return [] so the "no active package" warning shows — don't present the whole
    // catalogue as if they were entitled. Only fall back to the full list for the
    // data edge where they DO have active subs but none map to a category (e.g. a
    // package missing its sessionTypeId), to avoid a hard lock.
    if (formData.students.length > 0) {
      return activeStudentSubscriptions.length === 0 ? [] : mainSessionTypes;
    }

    return [];
  }, [
    mainSessionTypes,
    subPackages,
    activeStudentSubscriptions,
    formData.students,
    loadingStudentSubscriptions,
    studentSubscriptions.length,
    formData.sessionTypeId,
  ]);

  // 3. Filter Sub Items — ONLY show membership packages, never raw session types
  const availableSubItems = useMemo(() => {
    if (!subPackages || subPackages.length === 0) return [];
    let items = subPackages; // Start from packages only, not the merged sessionTypes list

    // Filter by Main Category
    if (formData.mainTypeId) {
      items = items.filter((item) => item.sessionTypeId === formData.mainTypeId);
    }

    // Filter by Student Subscriptions ONLY if no Package is currently selected
    if (formData.students.length > 0 && !formData.sessionTypeId) {
      const validPackageIds = new Set();

      activeStudentSubscriptions.forEach((sub) => {
        const pkgId = sub.membershipPackageId || sub.packageId || sub.membership_package_id;
        if (pkgId) validPackageIds.add(pkgId);
      });

      const filtered = items.filter((item) => validPackageIds.has(item.id));

      // Use the packages the student actually owns. If none match but the student has
      // NO active subscription at all, return [] (the warning will show). Only fall
      // back to the full category list for the data edge where they have active subs
      // that didn't map to a package id.
      if (filtered.length > 0) return filtered;
      if (activeStudentSubscriptions.length === 0) return [];
    }

    return items;
  }, [
    subPackages,
    formData.mainTypeId,
    formData.students,
    activeStudentSubscriptions,
    formData.sessionTypeId,
  ]);

  // --- End Filtering Logic ---

  // Initialize: Load all students if no filters applied initially?
  // Old logic: loaded students based on SessionType.
  // New logic: We want to select Student FIRST.
  // So we should load ALL students initially (or use `students` prop which comes from `useSessionData`).
  // `students` prop already has all students (limited by API pagination usually).
  // Use `students` from props for the list.

  const filteredStudents = useMemo(() => {
    // Logic 1: If Main Category Selected -> Use fetched availableStudents (by session type)
    let list = students || [];

    if (formData.mainTypeId) {
      // If main category selected, show students who have packages under that type
      list = availableStudents;
    }
    
    // Logic 3: (Start) -> Show All Students
    else {
      // existing list is fine
    }

    if (studentSearchTerm) {
      const lowerTerm = studentSearchTerm.trim().toLowerCase();
      list = list.filter((s) =>
        `${s.name} ${s.surname}`.toLowerCase().includes(lowerTerm),
      );
    }
    return list;
  }, [
    students,
    availableStudents,
    formData.mainTypeId,
    formData.students,
    studentSearchTerm,
  ]);

  if (!isOpen) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "selectedOrganizationId") {
      onFormDataChange((prev) => ({
        ...prev,
        [name]: value,
        sessionTypeId: "",
        mainTypeId: "",
        students: [],
      }));
      setStudentSearchTerm("");
      if (value && onLoadSessionTypes) {
        onLoadSessionTypes(value);
      }
    } else if (name === "mainTypeId") {
      // When Main Category changes, clear Sub Type
      onFormDataChange((prev) => ({
        ...prev,
        mainTypeId: value,
        sessionTypeId: "", // Clear sub selection
      }));
    } else {
      onFormDataChange((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleDateTimeChange = (field, value) => {
    if (field === "sessionDate") {
      const currentStartTime = moment(formData.startTime).format("HH:mm:ss");
      const currentEndTime = moment(formData.endTime).format("HH:mm:ss");

      onFormDataChange((prev) => ({
        ...prev,
        startTime: `${value}T${currentStartTime}`,
        endTime: `${value}T${currentEndTime}`,
      }));
    } else if (field === "startTime") {
      const currentDate = moment(formData.startTime).format("YYYY-MM-DD");
      onFormDataChange((prev) => ({
        ...prev,
        startTime: `${currentDate}T${value}:00`,
      }));
    } else if (field === "endTime") {
      const currentDate = moment(formData.endTime).format("YYYY-MM-DD");
      onFormDataChange((prev) => ({
        ...prev,
        endTime: `${currentDate}T${value}:00`,
      }));
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const SessionSummary = ({ session }) => {
    // ... (Keep existing summary logic)
    const getStatusBadge = (status) => {
      const badges = {
        scheduled: "px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800",
        completed: "px-2 py-1 text-xs rounded-full bg-green-100 text-green-800",
        cancelled: "px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800",
        "no-show": "px-2 py-1 text-xs rounded-full bg-red-100 text-red-800",
      };

      return (
        <span className={badges[status] || badges.scheduled}>
          {t(`sessionsPage.filters.statuses.${status}`)}
        </span>
      );
    };

    if (!session) return null;

    return (
      <div
        className={`${colors.bgTertiary} p-4 rounded-lg mb-4 border ${colors.border}`}
      >
        <div className="flex justify-between items-start mb-2">
          <h3 className={`text-lg font-semibold ${colors.textPrimary}`}>
            {session.studentName}
          </h3>
          {getStatusBadge(session.status)}
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <div>
            <span className={`font-medium ${colors.textMuted}`}>
              {t("sessionsPage.modal.trainer")}:
            </span>
            <span className={`ml-2 ${colors.textPrimary}`}>
              {session.trainerName}
            </span>
          </div>
          <div>
            <span className={`font-medium ${colors.textMuted}`}>
              {t("sessionsPage.modal.package")}:
            </span>
            <span className={`ml-2 ${colors.textPrimary}`}>
              {session.packageName}
            </span>
          </div>
          {/* ... other summary fields ... */}
          <div>
            <span className={`font-medium ${colors.textMuted}`}>
              {t("sessionsPage.modal.sessionDate")}:
            </span>
            <span className={`ml-2 ${colors.textPrimary}`}>
              {moment(session.start).format("MMM D, YYYY")}
            </span>
          </div>
          <div>
            <span className={`font-medium ${colors.textMuted}`}>
              {t("sessionsPage.modal.startTime")} /{" "}
              {t("sessionsPage.modal.endTime")}:
            </span>
            <span className={`ml-2 ${colors.textPrimary}`}>
              {moment(session.start).format("h:mm A")} -{" "}
              {moment(session.end).format("h:mm A")}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="modal-overlay" style={{ zIndex: 1000 }}>
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      <div
        className={`
        relative w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col
        ${colors.cardBg} ${colors.cardBorder}
        rounded-xl shadow-2xl border
        transform transition-all duration-200 ease-out scale-100
      `}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between p-4 border-b shrink-0 ${colors.border}`}
        >
          <h2 className={`text-lg font-bold ${colors.textPrimary}`}>
            {modalType === "create"
              ? "Yeni Ders Oluştur" // Check translation key
              : modalType === "group_manage"
                ? t("sessionsPage.modal.manageGroupTitle", "Manage Group")
                : modalType === "edit"
                  ? t("sessionsPage.modal.editTitle")
                  : t("sessionsPage.modal.detailsTitle")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${colors.textSecondary} hover:bg-gray-100 dark:hover:bg-zinc-800`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {modalType === "view" && selectedEvent && (
          <div className={`p-4 border-b shrink-0 ${colors.border}`}>
            <SessionSummary session={selectedEvent} />
          </div>
        )}

        {modalType === "group_manage" && (
          <div className="mx-4 mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start shrink-0">
            <Users className="w-5 h-5 text-blue-500 mr-2 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-bold text-blue-800">Grup Seansı Düzenleniyor</p>
              <p className="text-[11px] text-blue-700 mt-0.5">Yaptığınız değişiklikler (antrenör, paket, saat) gruptaki tüm öğrenciler için geçerli olacaktır.</p>
            </div>
          </div>
        )}

        {formError && (
          <div className="mx-4 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center shrink-0">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2 shrink-0" />
            <p className="text-sm text-red-700">{formError}</p>
          </div>
        )}

        <form
          onSubmit={handleFormSubmit}
          className="flex-1 flex flex-col overflow-hidden"
        >
          <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
            <div className="space-y-4">
              {/* Organization (All Orgs Mode) */}
              {isAllOrgsMode && modalType === "create" && (
                <div>
                  <label
                    htmlFor="selectedOrganizationId"
                    className={`block text-sm font-medium ${colors.textSecondary}`}
                  >
                    {t("sessionsPage.modal.organization")}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1 relative">
                    <Building2
                      className={`absolute left-3 top-3 ${colors.textMuted} w-4 h-4`}
                    />
                    <select
                      id="selectedOrganizationId"
                      name="selectedOrganizationId"
                      value={formData.selectedOrganizationId || ""}
                      onChange={handleInputChange}
                      className={`pl-10 block w-full rounded-md border ${formErrors.selectedOrganizationId ? "border-red-300" : colors.inputBorder} ${colors.inputBg} py-2 ${colors.inputText}`}
                    >
                      {organizations.map((org) => (
                        <option key={org.id} value={org.id}>
                          {org.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* 1. Trainer */}
              <div>
                <label
                  htmlFor="trainerId"
                  className={`block text-sm font-medium ${colors.textSecondary}`}
                >
                  {t("sessionsPage.modal.trainer")}
                </label>
                <div className="mt-1 relative">
                  <User
                    className={`absolute left-3 top-3 ${colors.textMuted} w-4 h-4`}
                  />
                  <select
                    id="trainerId"
                    name="trainerId"
                    value={formData.trainerId}
                    onChange={handleInputChange}
                    disabled={
                      modalType === "view" || (user?.role === 'trainer' && !user?.permissions?.includes('create:sessions'))
                    }
                    className={`pl-10 block w-full rounded-md border ${formErrors.trainerId ? "border-red-300" : colors.inputBorder} ${colors.inputBg} py-2 ${colors.inputText}`}
                  >
                    <option value="">
                      {t("sessionsPage.modal.placeholders.selectTrainer")}
                    </option>
                    {trainers.map((trainer) => (
                      <option key={trainer.id} value={trainer.id}>
                        {trainer.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* 3. Session Type - Split into Main Type and Sub Package */}
              <div className={`grid grid-cols-1 ${isDemoSession ? '' : 'md:grid-cols-2'} gap-4`}>
                {/* Main Category */}
                <div>
                  <label
                    className={`block text-sm font-medium ${colors.textSecondary}`}
                  >
                    {t("sessionsPage.modal.sessionType")}
                  </label>
                  <div className="mt-1 relative">
                    <Layers
                      className={`absolute left-3 top-3 ${colors.textMuted} w-4 h-4`}
                    />
                    <select
                      name="mainTypeId"
                      value={formData.mainTypeId}
                      onChange={handleInputChange}
                      disabled={
                        availableMainTypes.length === 0 || modalType === "view" || modalType === "group_manage"
                      }
                      className={`pl-10 block w-full rounded-md border ${colors.inputBorder} ${colors.inputBg} py-2 ${colors.inputText} disabled:opacity-50`}
                    >
                      <option value="">
                        {availableMainTypes.length === 0
                          ? formData.students.length > 0
                            ? "No Types Available"
                            : "Select Category"
                          : "Select Category"}
                      </option>
                      {availableMainTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Sub Package/Content */}
                {!isDemoSession && (
                <div>
                  <label
                    className={`block text-sm font-medium ${colors.textSecondary}`}
                  >
                    {t("sessionsPage.modal.package")}
                  </label>
                  <div className="mt-1 relative">
                    <Package
                      className={`absolute left-3 top-3 ${colors.textMuted} w-4 h-4`}
                    />
                    <select
                      name="sessionTypeId"
                      value={formData.sessionTypeId}
                      onChange={handleInputChange}
                      disabled={
                        (!formData.mainTypeId && !formData.sessionTypeId) || modalType === "view" || modalType === "group_manage"
                      }
                      className={`pl-10 block w-full rounded-md border ${formErrors.sessionTypeId ? "border-red-300" : colors.inputBorder} ${colors.inputBg} py-2 ${colors.inputText} disabled:opacity-50`}
                    >
                      <option value="">Select Package</option>
                      {availableSubItems.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                          {item.remainingSessions
                            ? ` (${item.remainingSessions} left)`
                            : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                  {formErrors.sessionTypeId && (
                    <p className="mt-1 text-sm text-red-600">
                      {formErrors.sessionTypeId}
                    </p>
                  )}
                </div>
                )}
              </div>

              {/* 2. Students (Moved to 2nd position, BEFORE Session Type) */}
              {!isDemoSession && (
                <div>
                  <label
                    className="form-label"
                  >
                  {t("sessionsPage.modal.students")}
                </label>

                {modalType === "create" || modalType === "group_manage" ? (
                  <div
                    className={`border ${colors.border} rounded-xl overflow-hidden`}
                  >
                    {/* Search Bar */}
                    <div
                      className={`p-2 border-b ${colors.border} ${colors.bgTertiary}`}
                    >
                      <input
                        type="text"
                        placeholder={t(
                          "sessionsPage.modal.searchStudentsPlaceholder",
                          "Öğrenci ara...",
                        )}
                        value={studentSearchTerm}
                        onChange={(e) => setStudentSearchTerm(e.target.value)}
                        className={`block w-full rounded-lg border-none ${colors.bgPrimary} py-2 px-3 text-sm focus:ring-2 focus:ring-blue-500`}
                      />
                    </div>

                    {/* Student List */}
                    <div
                      className={`max-h-60 overflow-y-auto ${colors.bgPrimary} divide-y ${colors.border}`}
                    >
                      {loadingStudents ? (
                        <div className="p-8 text-center">
                          <LoadingSpinner size="small" />
                          <p className={`mt-2 text-sm ${colors.textMuted}`}>Öğrenciler getiriliyor...</p>
                        </div>
                      ) : filteredStudents.length === 0 ? (
                        <div className="p-8 text-center flex flex-col items-center">
                          <User className={`w-8 h-8 ${colors.textMuted} mb-2 opacity-20`} />
                          <p className={`text-sm ${colors.textMuted}`}>
                            {studentSearchTerm
                              ? "Eşleşen öğrenci bulunamadı"
                              : "Gösterilecek öğrenci yok"}
                          </p>
                        </div>
                      ) : (
                        filteredStudents.map((student) => {
                          const isSelected = formData.students.includes(student.id);
                          const isMaxReached = currentPackage?.maxParticipants && 
                                             formData.students.length >= currentPackage.maxParticipants && 
                                             !isSelected;

                          return (
                            <label
                              key={student.id}
                              className={`flex items-center px-4 py-3 hover:${colors.bgHover} cursor-pointer transition-all ${isSelected ? 'bg-blue-500/5' : ''} ${isMaxReached ? 'opacity-50 grayscale-[0.5]' : ''}`}
                            >
                              <div className="relative flex items-center">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  disabled={isMaxReached}
                                  onChange={(e) => {
                                    const newStudents = e.target.checked
                                      ? [...formData.students, student.id]
                                      : formData.students.filter(
                                          (id) => id !== student.id,
                                        );
                                    handleStudentsChange(newStudents);
                                  }}
                                  className={`w-5 h-5 text-blue-600 rounded-lg focus:ring-blue-500 border-gray-300 transition-all ${isMaxReached ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                />
                              </div>
                              <div className="ml-4 flex-1">
                                <div
                                  className={`text-[14px] font-bold ${isSelected ? 'text-blue-500' : colors.textPrimary} transition-colors`}
                                >
                                  {student.name} {student.surname}
                                </div>
                                {student.phoneNumber && (
                                  <div className={`text-[11px] ${colors.textMuted} font-medium`}>
                                    {student.phoneNumber}
                                  </div>
                                )}
                              </div>
                              {isSelected && (
                                <div className="bg-blue-500/20 text-blue-500 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider">
                                  {t('sessionsPage.modal.selected', 'SEÇİLİ')}
                                </div>
                              )}
                              {isMaxReached && (
                                <div className="text-orange-500 text-[10px] font-bold uppercase tracking-tight ml-2">
                                  Limit Dolu
                                </div>
                              )}
                            </label>
                          );
                        })
                      )}
                    </div>

                    {/* Selected Summary */}
                    {/* Selected Summary */}
                    <div
                      className={`p-3 bg-blue-500/5 border-t ${colors.border} flex flex-col gap-2`}
                    >
                      <div className="flex justify-between items-center px-1">
                        <span className={`text-[11px] font-bold ${colors.textMuted} uppercase tracking-wider`}>
                          {t('sessionsPage.modal.currentMembers', 'Grup Üyeleri')} ({formData.students.length})
                        </span>
                        {currentPackage?.maxParticipants && (
                          <span className={`text-[10px] font-black ${formData.students.length >= currentPackage.maxParticipants ? 'text-orange-500' : 'text-blue-500'} uppercase`}>
                            Kapasite: {formData.students.length} / {currentPackage.maxParticipants}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {formData.students.length === 0 && (
                          <span className="text-xs text-orange-500 font-medium px-2 py-1 bg-orange-500/10 rounded-lg border border-orange-500/20 w-full text-center">
                            {t('sessionsPage.modal.placeholders.selectStudent', 'Öğrenci seçiniz')}
                          </span>
                        )}
                        {formData.students.map((sid) => {
                          const s = students.find((st) => st.id === sid);
                          return s ? (
                            <div
                              key={sid}
                              className="inline-flex items-center pl-2 pr-1 py-1 rounded-lg text-xs font-bold bg-blue-500 text-white shadow-sm transition-all hover:scale-105"
                            >
                              {s.name} {s.surname}
                              <button
                                type="button"
                                onClick={() =>
                                  handleStudentsChange(
                                    formData.students.filter((id) => id !== sid),
                                  )
                                }
                                className="ml-1.5 p-0.5 rounded-md hover:bg-white/20 transition-colors"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ) : null;
                        })}
                      </div>
                    </div>
                  </div>
                ) : (
                  // Edit Mode (Single Student)
                  <div className="relative">
                    <User
                      className={`absolute left-3 top-3 ${colors.textMuted} w-4 h-4`}
                    />
                    <select
                      value={formData.students[0] || ""}
                      onChange={(e) => handleStudentsChange([e.target.value])}
                      className={`pl-10 block w-full rounded-md border ${colors.inputBorder} ${colors.inputBg} py-2 ${colors.inputText}`}
                    >
                      <option value="">Select Student</option>
                      {students.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name} {s.surname}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {formErrors.students && (
                  <p className="mt-1 text-sm text-red-600">
                    {formErrors.students}
                  </p>
                )}

                {/* Info Alert: If student selected but no packages found (and no package selected) */}
                {formData.students.length > 0 &&
                  availableMainTypes.length === 0 &&
                  !loadingStudentSubscriptions &&
                  !formData.sessionTypeId && (
                    <div className="mt-2 p-2 rounded-lg bg-orange-50 border border-orange-200 flex items-start">
                      <AlertCircle className="w-4 h-4 text-orange-500 mr-2 mt-0.5" />
                      <p className="text-xs text-orange-700">
                        {t(
                          "sessionsPage.modal.noPackagesForStudent",
                          "Selected student(s) do not have any active packages.",
                        )}
                      </p>
                    </div>
                  )}
              </div>
              )}

              {/* Date & Time */}
              {(modalType === "create" || modalType === "edit" || modalType === "group_manage") && (
                <>
                  <div>
                    <label
                      htmlFor="sessionDate"
                      className={`block text-sm font-medium ${colors.textSecondary}`}
                    >
                      {t("sessionsPage.modal.sessionDate")}
                    </label>
                    <div className="mt-1 relative">
                      <CalendarIcon
                        className={`absolute left-3 top-3 ${colors.textMuted} w-4 h-4`}
                      />
                      <input
                        type="date"
                        id="sessionDate"
                        value={
                          formData.startTime
                            ? moment(formData.startTime).format("YYYY-MM-DD")
                            : ""
                        }
                        onChange={(e) =>
                          handleDateTimeChange("sessionDate", e.target.value)
                        }
                        className={`pl-10 block w-full rounded-md border ${formErrors.startTime ? "border-red-300" : colors.inputBorder} ${colors.inputBg} py-2 ${colors.inputText}`}
                      />
                    </div>
                  </div>

                  <div className="modal-grid">
                    <TimePicker
                      label={t("sessionsPage.modal.startTime")}
                      value={moment(formData.startTime).format("HH:mm")}
                      onChange={(val) => handleDateTimeChange("startTime", val)}
                      error={formErrors.startTime}
                    />
                    <TimePicker
                      label={t("sessionsPage.modal.endTime")}
                      value={moment(formData.endTime).format("HH:mm")}
                      onChange={(val) => handleDateTimeChange("endTime", val)}
                      error={formErrors.endTime}
                    />
                  </div>
                </>
              )}

              {/* Status (Edit only) */}
              {modalType === "edit" && (
                <div>
                  <label
                    htmlFor="status"
                    className={`block text-sm font-medium ${colors.textSecondary}`}
                  >
                    {t("sessionsPage.modal.status")}
                  </label>
                  <select
                    name="status"
                    value={formData.status || "scheduled"}
                    onChange={handleInputChange}
                    className={`block w-full rounded-md border ${colors.inputBorder} ${colors.inputBg} py-2 px-3 ${colors.inputText}`}
                  >
                    <option value="scheduled">
                      {t("sessionsPage.filters.statuses.scheduled")}
                    </option>
                    <option value="completed">
                      {t("sessionsPage.filters.statuses.completed")}
                    </option>
                    <option value="cancelled">
                      {t("sessionsPage.filters.statuses.cancelled")}
                    </option>
                    <option value="no-show">
                      {t("sessionsPage.filters.statuses.noShow")}
                    </option>
                  </select>
                </div>
              )}

              {modalType !== "group_manage" && (
                <div>
                  <label
                    htmlFor="notes"
                    className={`block text-sm font-medium ${colors.textSecondary}`}
                  >
                    {t("sessionsPage.modal.notes")}
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    rows={3}
                    value={formData.notes}
                    onChange={handleInputChange}
                    className={`block w-full rounded-md border ${colors.inputBorder} ${colors.inputBg} py-2 px-3 ${colors.inputText}`}
                    placeholder={t("sessionsPage.modal.notesPlaceholder")}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Footer Actions */}
          <div
            className={`p-4 border-t ${colors.border} flex flex-wrap items-center gap-3 shrink-0`}
          >
            {modalType === "edit" && (
              <button
                type="button"
                onClick={onDelete}
                className="flex items-center justify-center px-4 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-colors shrink-0"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {t("sessionsPage.modal.buttons.delete")}
              </button>
            )}
            {/* Main actions — wrap under the delete button on narrow screens */}
            <div className="flex gap-3 flex-1 justify-end min-w-[220px]">
              <button
                type="button"
                onClick={onClose}
                className={`px-4 py-2 flex-1 sm:flex-none sm:min-w-[120px] ${colors.bgTertiary} hover:${colors.bgHover} border ${colors.border} rounded-lg transition-colors text-center flex items-center justify-center`}
              >
                {t("sessionsPage.modal.buttons.cancel")}
              </button>
              <button
                type="submit"
                disabled={submitting || (currentPackage?.maxParticipants && formData.students.length > currentPackage.maxParticipants)}
                className={`px-4 sm:px-6 py-2 flex-1 sm:flex-none sm:min-w-[120px] rounded-lg transition-all transform hover:scale-105 text-center flex items-center justify-center ${
                  submitting || (currentPackage?.maxParticipants && formData.students.length > currentPackage.maxParticipants)
                    ? "bg-gray-400 text-white opacity-50 cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-500/30"
                }`}
              >
                {submitting
                  ? "..."
                  : modalType === "create"
                    ? t("sessionsPage.modal.buttons.create")
                    : t("sessionsPage.modal.buttons.save")}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SessionModal;
